import { LANGUAGES } from "@bt/parts-core";
import { z } from "zod";
import { id } from "./schemas/common";
import { garageNameInput } from "./schemas/entities";

/**
 * Accounts on a hosted server (ADR 0011): who may do what. Signing in is the identity provider's
 * job; the roles, memberships and rules here are the app's own.
 */

/** Platform roles: an admin runs the admin interface and can open every garage. */
export const PLATFORM_ROLES = ["admin"] as const;
export type PlatformRole = (typeof PLATFORM_ROLES)[number];

/**
 * Roles within one garage: a garage admin also manages its members and garage settings; a visitor
 * tries the app in a demo garage of their own (the public demo, ADR 0012).
 */
export const GARAGE_ROLES = ["garage_admin", "garage_user", "garage_visitor"] as const;
export type GarageRole = (typeof GARAGE_ROLES)[number];

/** The roles an invitation or a garage admin gives; only the public demo makes visitors. */
export const MEMBER_ROLES = [
  "garage_admin",
  "garage_user",
] as const satisfies readonly GarageRole[];
export type MemberRole = (typeof MEMBER_ROLES)[number];

/**
 * A disabled user was switched off by an admin; a deleted one was deleted by an admin, keeping
 * only a small record. Neither may use anything (the restricted state).
 */
export const USER_STATUSES = ["active", "disabled", "deleted"] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

/** How the app looks: following the device's setting, or always light or dark. */
export const THEMES = ["system", "light", "dark"] as const;
export type Theme = (typeof THEMES)[number];

/** A deactivated member keeps their place in the garage but can't open it. */
export const MEMBER_STATUSES = ["active", "deactivated"] as const;
export type MemberStatus = (typeof MEMBER_STATUSES)[number];

/** What a request may do in one garage. */
export type GarageAccess =
  /** Single-user installations (desktop app, local development): everything. */
  | { kind: "full" }
  /** A platform admin, member or not. */
  | { kind: "admin" }
  | { kind: "member"; role: GarageRole }
  /** Not a member, a deactivated member, or a user who isn't active. */
  | { kind: "none" };

/** Each garage permission and the garage roles that have it. Later roles and permissions go here. */
export const GARAGE_PERMISSIONS = {
  "garage.use": ["garage_admin", "garage_user", "garage_visitor"],
  /** Change the garage settings, replace the data with a package: part of trying the app. */
  "garage.manage": ["garage_admin", "garage_visitor"],
  /** Rename the garage, which changes its address; a demo garage keeps its name. */
  "garage.rename": ["garage_admin"],
  /** Invite, change and remove members: never a visitor, so the demo mails no one they name. */
  "members.manage": ["garage_admin"],
} as const satisfies Record<string, readonly GarageRole[]>;
export type GaragePermission = keyof typeof GARAGE_PERMISSIONS;

export function can(access: GarageAccess, permission: GaragePermission): boolean {
  switch (access.kind) {
    case "full":
    case "admin":
      return true;
    case "member":
      return (GARAGE_PERMISSIONS[permission] as readonly GarageRole[]).includes(access.role);
    case "none":
      return false;
  }
}

/** A user's access to a garage, from their account and their membership there (if any). */
export function garageAccess(
  user: { status: UserStatus; platformRole: PlatformRole | null },
  membership: { role: GarageRole; status: MemberStatus } | null,
): GarageAccess {
  if (user.status !== "active") return { kind: "none" };
  if (user.platformRole === "admin") return { kind: "admin" };
  if (membership?.status === "active") return { kind: "member", role: membership.role };
  return { kind: "none" };
}

/**
 * Users on the desktop app (ADR 0013) have no memberships: every garage on the computer is
 * everyone's to use, and admins also manage it.
 */
export function localGarageAccess(user: {
  status: UserStatus;
  platformRole: PlatformRole | null;
}): GarageAccess {
  return garageAccess(user, { role: "garage_user", status: "active" });
}

/** A PIN: digits only, asked for in the desktop app's user picker (ADR 0013). */
export const PIN_MIN = 4;
export const PIN_MAX = 8;

/** After this many wrong PINs in a row, a user waits PIN_WAIT_MINUTES before trying again. */
export const PIN_ATTEMPTS = 5;
export const PIN_WAIT_MINUTES = 5;

/** Until when a user has to wait after `failures` wrong PINs in a row; null while they may try. */
export function pinWaitUntil(failures: number, now: Date): Date | null {
  return failures >= PIN_ATTEMPTS ? new Date(now.getTime() + PIN_WAIT_MINUTES * 60 * 1000) : null;
}

/**
 * The desktop app's recovery code (ADR 0013): shown once when users are switched on, it removes a
 * forgotten PIN when no admin can, and is replaced by a new one each time it is used. Letters and
 * digits that can't be mistaken for each other, in groups of four.
 */
export const RECOVERY_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
export const RECOVERY_GROUPS = 3;

/** A typed recovery code as stored: without spaces or dashes, in capitals. */
export function normalizeRecoveryCode(code: string): string {
  return code.replace(/[\s-]/g, "").toUpperCase();
}

/** "ABCD-EFGH-JKLM", for showing. */
export function formatRecoveryCode(code: string): string {
  return (
    normalizeRecoveryCode(code)
      .match(/.{1,4}/g)
      ?.join("-") ?? ""
  );
}

/**
 * Whom the desktop app opens for without the user picker, among the users who may sign in: the
 * only one, as long as they have no PIN. With anyone else to choose from, or a PIN to ask for, the
 * picker shows.
 */
export function userToOpenFor<T extends { hasPin: boolean }>(users: readonly T[]): T | null {
  const [only] = users;
  return users.length === 1 && only && !only.hasPin ? only : null;
}

export type MemberChange =
  | { kind: "role"; role: GarageRole }
  | { kind: "deactivate" }
  | { kind: "reactivate" }
  | { kind: "remove" }
  /** The member deletes their own account: the right to erasure comes first. */
  | { kind: "deleteOwnAccount" };

/**
 * Whether a change would leave a garage that has an active garage admin without one. A garage
 * that already has none (its last garage admin deleted their account) isn't blocked further.
 */
export function lastGarageAdminProblem(
  members: readonly { userId: string; role: GarageRole; status: MemberStatus }[],
  userId: string,
  change: MemberChange,
): boolean {
  if (change.kind === "deleteOwnAccount") return false;
  const isActiveAdmin = (m: { role: GarageRole; status: MemberStatus }) =>
    m.role === "garage_admin" && m.status === "active";
  if (!members.some(isActiveAdmin)) return false;
  const after = members.flatMap((m) => {
    if (m.userId !== userId) return [m];
    if (change.kind === "remove") return [];
    if (change.kind === "role") return [{ ...m, role: change.role }];
    const status: MemberStatus = change.kind === "deactivate" ? "deactivated" : "active";
    return [{ ...m, status }];
  });
  return !after.some(isActiveAdmin);
}

export type PlatformAdminChange = "removeRole" | "disable" | "delete" | "deleteOwnAccount";

/** The last active platform admin can't lose the role, be disabled or be deleted: someone has to run the service. */
export function lastPlatformAdminProblem(
  admins: readonly { userId: string; status: UserStatus }[],
  userId: string,
  _change: PlatformAdminChange,
): boolean {
  const active = admins.filter((a) => a.status === "active");
  return active.length === 1 && active[0]?.userId === userId;
}

export const INVITE_DAYS_DEFAULT = 7;
export const INVITE_DAYS_MIN = 1;
export const INVITE_DAYS_MAX = 90;

/** When an invitation made now runs out. */
export function inviteExpiry(now: Date, days: number): Date {
  return new Date(now.getTime() + days * 24 * 60 * 60 * 1000);
}

export type InvitationState = "pending" | "expired" | "accepted" | "revoked";

export function invitationState(
  invitation: { acceptedAt: string | null; revokedAt: string | null; expiresAt: string },
  now: Date,
): InvitationState {
  if (invitation.acceptedAt) return "accepted";
  if (invitation.revokedAt) return "revoked";
  return new Date(invitation.expiresAt).getTime() <= now.getTime() ? "expired" : "pending";
}

/** Long enough is all a password needs to be; no character rules (NIST SP 800-63B). */
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX = 128;

export function passwordProblem(password: string): "tooShort" | "tooLong" | null {
  if (password.length < PASSWORD_MIN) return "tooShort";
  if (password.length > PASSWORD_MAX) return "tooLong";
  return null;
}

export const email = z
  .string()
  .trim()
  .toLowerCase()
  .max(254, { error: "validation.tooLong" })
  .pipe(z.email({ error: "validation.email" }));

/** A new password: checked against the policy before the identity provider sees it. */
export const newPassword = z
  .string()
  .min(PASSWORD_MIN, { error: "validation.passwordTooShort" })
  .max(PASSWORD_MAX, { error: "validation.tooLong" });

/** A password typed to prove who you are; the policy isn't checked again. */
export const currentPassword = z
  .string()
  .min(1, { error: "validation.required" })
  .max(PASSWORD_MAX, { error: "validation.tooLong" });

export const personName = z
  .string()
  .trim()
  .min(1, { error: "validation.required" })
  .max(100, { error: "validation.tooLong" });

/** A last name on the desktop app, where a first name is enough; empty is none. */
export const optionalPersonName = z
  .string()
  .trim()
  .max(100, { error: "validation.tooLong" })
  .nullish()
  .transform((value) => value || null);

export const pin = z
  .string()
  .regex(new RegExp(`^\\d{${PIN_MIN},${PIN_MAX}}$`), { error: "validation.pin" });

/** One-time tokens from email links. */
export const linkToken = z.string().trim().min(1).max(200);

export const signInInput = z.object({ email, password: currentPassword });
export const profileInput = z.object({ firstName: personName, lastName: personName });
export type ProfileInput = z.infer<typeof profileInput>;
export const changePasswordInput = z.object({ current: currentPassword, next: newPassword });
export const requestPasswordResetInput = z.object({ email, language: z.enum(LANGUAGES) });
export const resetPasswordInput = z.object({ token: linkToken, password: newPassword });
export const deleteAccountInput = z.object({ password: currentPassword });

export const inviteInput = z.object({
  email,
  firstName: personName,
  lastName: personName,
  role: z.enum(MEMBER_ROLES),
});
export type InviteInput = z.infer<typeof inviteInput>;

/**
 * Accepting an invitation: a new account chooses a password; an existing account signs in with
 * its password, or needs none when it is already signed in. The names are the invitation's,
 * possibly corrected; an existing active account keeps its own.
 */
export const acceptInvitationInput = z.object({
  token: linkToken,
  firstName: personName,
  lastName: personName,
  password: z.string().max(PASSWORD_MAX, { error: "validation.tooLong" }).nullish(),
});

/**
 * A new garage, with first users to invite or none. Without an invited garage admin, the admin
 * who creates it becomes its garage admin.
 */
export const adminCreateGarageInput = z.object({
  name: garageNameInput.shape.name,
  users: z
    .array(inviteInput)
    .max(20)
    .refine((users) => new Set(users.map((u) => u.email)).size === users.length, {
      error: "validation.emailTwice",
    }),
  language: z.enum(LANGUAGES),
});
export type AdminCreateGarageInput = z.infer<typeof adminCreateGarageInput>;

/** A user's own language and theme: the welcome steps and the profile. */
export const preferencesInput = z.object({
  language: z.enum(LANGUAGES),
  theme: z.enum(THEMES),
});
export type Preferences = z.infer<typeof preferencesInput>;

/** A user's name on the desktop app (ADR 0013): switching users on, adding one, the profile. */
export const localNameInput = z.object({ firstName: personName, lastName: optionalPersonName });
export type LocalNameInput = z.infer<typeof localNameInput>;
/** An admin adds a user on the desktop app; the PIN is theirs to choose. */
export const localUserInput = localNameInput.extend({ admin: z.boolean() });
export type LocalUserInput = z.infer<typeof localUserInput>;
/** Picking yourself in the user picker; a wrong PIN of any shape counts as wrong. */
export const localSignInInput = z.object({
  userId: id,
  pin: z.string().max(PIN_MAX, { error: "validation.tooLong" }).nullable(),
});
/** Choosing, changing or (null) removing your own PIN. */
export const pinInput = z.object({ pin: pin.nullable() });
/** "Forgot your PIN?" in the user picker: the recovery code removes it. */
export const recoverPinInput = z.object({
  userId: id,
  code: z.string().max(40, { error: "validation.tooLong" }),
});

/** A passkey's name, e.g. the device it is on (ADR 0011). */
export const PASSKEY_NAME_MAX = 60;
export const passkeyName = z
  .string()
  .trim()
  .min(1, { error: "validation.required" })
  .max(PASSKEY_NAME_MAX, { error: "validation.tooLong" });
/** The browser's answer to a passkey ceremony, handed on to the identity provider as it is. */
export const passkeyResponse = z.record(z.string(), z.unknown());
export const passkeySignInInput = z.object({ response: passkeyResponse });
export const addPasskeyInput = z.object({ response: passkeyResponse, name: passkeyName });
export const renamePasskeyInput = z.object({ id, name: passkeyName });
export const removePasskeyInput = z.object({ id });
/**
 * Whether to offer someone a passkey once (ADR 0011): only where the sign-in has passkeys, not to
 * a visitor of the public demo, not to someone who has one, and never again once they answered.
 */
export function passkeyOfferDue(person: {
  passkeysAvailable: boolean;
  visitor: boolean;
  passkeys: number;
  offered: boolean;
}): boolean {
  return person.passkeysAvailable && !person.visitor && person.passkeys === 0 && !person.offered;
}
/** Confirming the password before adding a passkey on a session older than a day. */
export const confirmPasswordInput = z.object({ password: currentPassword });

export const platformSettingsInput = z.object({
  inviteDays: z
    .int({ error: "validation.number" })
    .min(INVITE_DAYS_MIN, { error: "validation.inviteDays" })
    .max(INVITE_DAYS_MAX, { error: "validation.inviteDays" }),
});
export type PlatformSettings = z.infer<typeof platformSettingsInput>;

/** The filters of `/admin` › Users (ADR 0016), also as `?filter=` so the admin app can link to them. */
export const USER_ACTIVITY_FILTERS = ["never-back", "quiet", "invited"] as const;
export type UserActivityFilter = (typeof USER_ACTIVITY_FILTERS)[number];

/** Gone quiet: not seen for this many days. */
export const QUIET_DAYS = 90;

/** When someone was last seen: the UTC day, and whether that is their last sign-in instead. */
export interface LastSeen {
  on: string;
  /** Not seen since tracking began: the day of their last sign-in, labelled as such. */
  beforeTracking: boolean;
}

/**
 * The last UTC day someone used the app (`last_seen_on`), or, before tracking began, the day of
 * their last sign-in; null for someone never signed in.
 */
export function lastSeen(user: {
  lastSeenOn: string | null;
  lastLoginAt: string | null;
}): LastSeen | null {
  if (user.lastSeenOn) return { on: user.lastSeenOn, beforeTracking: false };
  if (user.lastLoginAt) return { on: user.lastLoginAt.slice(0, 10), beforeTracking: true };
  return null;
}

/**
 * Whether a user belongs under a filter on UTC day `today`: "never came back" was seen only on
 * the day they joined, which lies before today; "gone quiet" wasn't seen for `QUIET_DAYS`. Deleted
 * users belong under neither; "invited" lists invitations, not users.
 */
export function matchesActivityFilter(
  user: {
    status: UserStatus;
    createdAt: string;
    lastSeenOn: string | null;
    lastLoginAt: string | null;
  },
  filter: UserActivityFilter,
  today: string,
): boolean {
  if (user.status === "deleted" || filter === "invited") return false;
  const seen = lastSeen(user)?.on ?? null;
  const joined = user.createdAt.slice(0, 10);
  if (filter === "never-back") return joined < today && (seen === null || seen <= joined);
  const quietSince = new Date(Date.parse(`${today}T00:00:00Z`) - QUIET_DAYS * 86_400_000)
    .toISOString()
    .slice(0, 10);
  return seen === null || seen < quietSince;
}
